import { useRef, useState, useCallback, useEffect } from "react";
import type { MeetingRoom, CalendarEvent } from "@/types/calendar";
import { EventBlock } from "./EventBlock";
import { CALENDAR_CONFIG, BOOKING_CONFIG } from "@/config/rooms";
import { cn } from "@/lib/utils";

const SLOT_HEIGHT_PX = CALENDAR_CONFIG.hourHeight / 4;
const SLOT_MIN = 15;

interface DragState {
  startSlot: number;
  currentSlot: number;
  startY: number;
}

export interface MoveDragGhost {
  topPx: number;
  heightPx: number;
  valid: boolean;
  reason?: string;
  label: string;
  timeLabel: string;
}

interface RoomColumnProps {
  room: MeetingRoom;
  events: CalendarEvent[];
  isFirst: boolean;
  currentTime: Date;
  onEventClick?: (event: CalendarEvent, room: MeetingRoom) => void;
  onSlotSelect?: (roomId: string, startISO: string, endISO: string) => void;
  registerColumnRef?: (roomId: string, el: HTMLDivElement | null) => void;
  moveDragGhost?: MoveDragGhost | null;
  draggingEventId?: string | null;
  isMovableEvent?: (event: CalendarEvent) => boolean;
  onMoveDragStart?: (
    event: CalendarEvent,
    fromRoomId: string,
    e: React.PointerEvent<HTMLDivElement>,
  ) => void;
}

function slotToDate(slot: number): Date {
  const totalMin = CALENDAR_CONFIG.startHour * 60 + slot * SLOT_MIN;
  const d = new Date();
  d.setHours(Math.floor(totalMin / 60), totalMin % 60, 0, 0);
  return d;
}

function ceil15Slot(d: Date): number {
  const totalMin = d.getHours() * 60 + d.getMinutes() + (d.getSeconds() > 0 ? 1 : 0);
  const slotMin = CALENDAR_CONFIG.startHour * 60;
  return Math.ceil((totalMin - slotMin) / SLOT_MIN);
}

function yToSlot(y: number): number {
  return Math.floor(y / SLOT_HEIGHT_PX);
}

function totalSlots(): number {
  return (CALENDAR_CONFIG.endHour - CALENDAR_CONFIG.startHour) * 4;
}

function slotsOverlapEvents(fromSlot: number, toSlot: number, events: CalendarEvent[]): boolean {
  const minSlot = Math.min(fromSlot, toSlot);
  const maxSlot = Math.max(fromSlot, toSlot);
  const startDate = slotToDate(minSlot);
  const endDate = slotToDate(maxSlot + 1);
  return events.some((ev) => ev.start < endDate && ev.end > startDate);
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}분`;
  if (minutes % 60 === 0) return `${minutes / 60}시간`;
  return `${Math.floor(minutes / 60)}시간 ${minutes % 60}분`;
}

function formatSlotTime(slot: number): string {
  const d = slotToDate(slot);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function RoomColumn({
  room,
  events,
  isFirst,
  currentTime,
  onEventClick,
  onSlotSelect,
  registerColumnRef,
  moveDragGhost,
  draggingEventId,
  isMovableEvent,
  onMoveDragStart,
}: RoomColumnProps) {
  const colRef = useRef<HTMLDivElement>(null);
  const [hoverSlot, setHoverSlot] = useState<number | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const minSelectableSlot = ceil15Slot(currentTime);
  const maxSlot = totalSlots() - 1;
  const pastHeight = Math.max(0, minSelectableSlot * SLOT_HEIGHT_PX);

  useEffect(() => {
    registerColumnRef?.(room.id, colRef.current);
    return () => registerColumnRef?.(room.id, null);
  }, [registerColumnRef, room.id]);

  const getRelativeY = useCallback((clientY: number): number => {
    const rect = colRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    return clientY - rect.top;
  }, []);

  const clampSlot = useCallback((slot: number): number => {
    return Math.max(minSelectableSlot, Math.min(slot, maxSlot));
  }, [minSelectableSlot, maxSlot]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const y = getRelativeY(e.clientY);
    const rawSlot = yToSlot(y);

    if (drag) {
      const clampedSlot = clampSlot(rawSlot);
      const maxSlotsFromStart = BOOKING_CONFIG.maxDurationMin / SLOT_MIN - 1;
      const finalSlot = clampedSlot > drag.startSlot
        ? Math.min(clampedSlot, drag.startSlot + maxSlotsFromStart)
        : Math.max(clampedSlot, drag.startSlot - maxSlotsFromStart);
      setDrag((prev) => prev ? { ...prev, currentSlot: finalSlot } : prev);
      return;
    }

    if (e.pointerType !== "mouse") return;
    if (rawSlot < minSelectableSlot || rawSlot > maxSlot) {
      setHoverSlot(null);
      return;
    }
    const slotOverlaps = slotsOverlapEvents(rawSlot, rawSlot, events);
    setHoverSlot(slotOverlaps ? null : rawSlot);
  }, [drag, getRelativeY, clampSlot, minSelectableSlot, maxSlot, events]);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const y = getRelativeY(e.clientY);
    const rawSlot = yToSlot(y);
    if (rawSlot < minSelectableSlot) return;
    const slot = clampSlot(rawSlot);

    if (e.pointerType === "mouse") {
      e.currentTarget.setPointerCapture(e.pointerId);
    }

    setDrag({
      startSlot: slot,
      currentSlot: slot,
      startY: e.clientY,
    });
    setHoverSlot(null);
  }, [getRelativeY, clampSlot, minSelectableSlot]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag) return;

    const movedPx = Math.abs(e.clientY - drag.startY);
    const slotChanged = drag.startSlot !== drag.currentSlot;
    if (movedPx <= 3 || !slotChanged) {
      setDrag(null);
      return;
    }

    const fromSlot = Math.min(drag.startSlot, drag.currentSlot);
    const toSlot = Math.min(Math.max(drag.startSlot, drag.currentSlot), maxSlot);
    if (fromSlot < minSelectableSlot) {
      setDrag(null);
      return;
    }
    if (slotsOverlapEvents(fromSlot, toSlot, events)) {
      setDrag(null);
      return;
    }

    const startDate = slotToDate(fromSlot);
    const endDate = slotToDate(toSlot + 1);
    onSlotSelect?.(room.id, startDate.toISOString(), endDate.toISOString());
    setDrag(null);
  }, [drag, events, maxSlot, minSelectableSlot, room.id, onSlotSelect]);

  const handlePointerCancel = useCallback(() => {
    setDrag(null);
  }, []);

  const handlePointerLeave = useCallback(() => {
    if (!drag) setHoverSlot(null);
  }, [drag]);

  useEffect(() => {
    if (!drag) return;
    const handleGlobalUp = () => setDrag(null);
    window.addEventListener("pointerup", handleGlobalUp);
    return () => window.removeEventListener("pointerup", handleGlobalUp);
  }, [drag]);

  const dragFromSlot = drag ? Math.min(drag.startSlot, drag.currentSlot) : null;
  const dragToSlot = drag ? Math.max(drag.startSlot, drag.currentSlot) : null;
  const dragOverlaps = drag && dragFromSlot !== null && dragToSlot !== null
    ? slotsOverlapEvents(dragFromSlot, dragToSlot, events)
    : false;

  const ghostSlot = drag === null ? hoverSlot : null;
  const ghostOverlaps = ghostSlot !== null ? slotsOverlapEvents(ghostSlot, ghostSlot, events) : false;

  const isDragging = drag !== null;
  const moveActive = !!moveDragGhost;

  return (
    <div
      ref={colRef}
      className={cn(
        "flex-1 min-w-0 relative select-none",
        !isFirst && "border-l border-border/30",
        isDragging ? "cursor-ns-resize" : "cursor-crosshair",
        moveActive && "bg-foreground/[0.02]",
      )}
      onPointerMove={handlePointerMove}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onPointerLeave={handlePointerLeave}
    >
      {/* Past area: swallows clicks and shows default cursor */}
      {pastHeight > 0 && (
        <div
          className="absolute left-0 right-0 top-0 cursor-default"
          style={{ height: `${pastHeight}px` }}
          onPointerDown={(e) => e.stopPropagation()}
        />
      )}

      {/* Hover ghost (mouse only, no drag active) */}
      {ghostSlot !== null && !isDragging && !moveActive && (
        <div
          className="absolute left-0.5 right-0.5 md:left-1 md:right-1 pointer-events-none z-20 rounded"
          style={{
            top: `${ghostSlot * SLOT_HEIGHT_PX}px`,
            height: `${SLOT_HEIGHT_PX}px`,
            border: `1.5px dashed ${room.color}66`,
            backgroundColor: `${room.color}1A`,
            opacity: ghostOverlaps ? 0 : 1,
            transition: "opacity 120ms ease-out, background-color 120ms ease-out",
          }}
        >
          <div
            className="flex items-center justify-center gap-1 h-full"
            style={{ color: room.color }}
          >
            <span className="text-[10px] font-bold leading-none">+</span>
            <span className="text-[9px] md:text-[10px] font-medium truncate leading-none">새 예약</span>
          </div>
        </div>
      )}

      {/* Active drag ghost (new-booking selection) */}
      {isDragging && dragFromSlot !== null && dragToSlot !== null && (
        <div
          className="absolute left-0.5 right-0.5 md:left-1 md:right-1 pointer-events-none z-20 rounded overflow-hidden"
          style={{
            top: `${dragFromSlot * SLOT_HEIGHT_PX}px`,
            height: `${(dragToSlot - dragFromSlot + 1) * SLOT_HEIGHT_PX}px`,
            border: `1.5px solid ${room.color}`,
            backgroundColor: dragOverlaps ? "rgba(239,68,68,0.20)" : `${room.color}40`,
            transition: "background-color 120ms ease-out",
          }}
        >
          <div
            className="px-1 pt-0.5"
            style={{ color: dragOverlaps ? "#ef4444" : room.color }}
          >
            <div className="text-[10px] md:text-xs font-medium leading-tight truncate">
              {formatSlotTime(dragFromSlot)} – {formatSlotTime(dragToSlot + 1)}
            </div>
            <div
              className="text-[9px] md:text-[10px] leading-tight opacity-80 truncate"
              style={{ color: dragOverlaps ? "#ef4444" : room.color }}
            >
              {formatDuration((dragToSlot - dragFromSlot + 1) * SLOT_MIN)}
            </div>
          </div>
        </div>
      )}

      {/* Move-drag drop ghost (event being moved into this column) */}
      {moveDragGhost && (
        <div
          className="absolute left-0.5 right-0.5 md:left-1 md:right-1 pointer-events-none z-30 rounded overflow-hidden shadow-lg"
          style={{
            top: `${moveDragGhost.topPx}px`,
            height: `${moveDragGhost.heightPx}px`,
            border: `2px ${moveDragGhost.valid ? "solid" : "dashed"} ${moveDragGhost.valid ? room.color : "#ef4444"}`,
            backgroundColor: moveDragGhost.valid
              ? `${room.color}55`
              : "rgba(239,68,68,0.20)",
            transition: "background-color 120ms ease-out, top 80ms ease-out",
          }}
        >
          <div
            className="px-1.5 pt-0.5"
            style={{ color: moveDragGhost.valid ? room.color : "#ef4444" }}
          >
            <div className="text-[10px] md:text-xs font-semibold leading-tight truncate">
              {moveDragGhost.label}
            </div>
            <div className="text-[9px] md:text-[10px] leading-tight opacity-80 truncate">
              {moveDragGhost.timeLabel}
            </div>
            {!moveDragGhost.valid && moveDragGhost.reason && (
              <div className="text-[9px] md:text-[10px] leading-tight mt-0.5 truncate">
                {moveDragGhost.reason}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Events */}
      {events.map((event) => (
        <EventBlock
          key={event.id}
          event={event}
          color={room.color}
          isDraggingSelf={draggingEventId === event.id}
          isMovable={isMovableEvent ? isMovableEvent(event) : false}
          onClick={() => onEventClick?.(event, room)}
          onMoveDragStart={(ev, e) => onMoveDragStart?.(ev, room.id, e)}
        />
      ))}
    </div>
  );
}
